import React, { useState } from 'react';
import { ArrowRight, ArrowLeft, TrendingUp, Shield, DollarSign, Award, Users, CheckCircle, BarChart3, Calculator, FileText, Clock, Globe, Zap, Building, Leaf, Star, Target, Info, ChevronDown, ChevronUp } from 'lucide-react';

interface ProduitsExplicationProps {
  onNavigateBack: () => void;
  onStartSimulator: () => void;
}

const ProduitsExplication: React.FC<ProduitsExplicationProps> = ({
  onNavigateBack,
  onStartSimulator
}) => {
  const [activeProduct, setActiveProduct] = useState<'scpi' | 'gfi' | 'sukuk' | null>(null);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50">
      {/* Header avec navigation */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <button
              onClick={onNavigateBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-teal-600 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Retour</span>
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Nos Produits d'Investissement</h1>
            <div className="w-20"></div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-teal-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Découvrez nos Solutions d'Investissement
          </h2>
          <p className="text-xl md:text-2xl text-teal-100 mb-8 max-w-4xl mx-auto">
            Trois produits d'investissement conformes à vos valeurs, adaptés à tous les profils et objectifs financiers
          </p>
          <div className="flex justify-center">
            <div className="bg-white/10 backdrop-blur-sm rounded-full px-6 py-3">
              <span className="text-teal-200 font-medium">✓ Conformes aux principes éthiques</span>
            </div>
          </div>
        </div>
      </section>

      {/* Section principale des produits */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Introduction */}
          <div className="text-center mb-16">
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              Trois Solutions, Un Objectif : Votre Réussite Financière
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Chaque produit a été soigneusement sélectionné pour offrir des rendements attractifs
              tout en respectant les principes d'investissement éthique et responsable.
            </p>
          </div>

          {/* Grille des produits */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">

            {/* SCPI */}
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
              <div className="relative h-64">
                <img
                  src="https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=400&q=90"
                  alt="SCPI - Immobilier"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4">
                  <span className="badge-emerald">
                    SCPI
                  </span>
                </div>
                <div className="absolute bottom-4 right-4">
                  <div className="stats-card">
                    <div className="stats-value">4.5%</div>
                    <div className="stats-label">Rendement</div>
                  </div>
                </div>
              </div>
              <div className="p-8">
                <h4 className="text-2xl font-bold text-gray-900 mb-4">
                  SCPI - Immobilier Locatif
                </h4>
                <p className="text-gray-600 mb-6">
                  Investissement dans l'immobilier commercial et résidentiel avec des revenus locatifs réguliers et garantis.
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center text-sm">
                    <CheckCircle className="w-4 h-4 text-emerald-500 mr-2" />
                    <span>Revenus locatifs mensuels</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <CheckCircle className="w-4 h-4 text-emerald-500 mr-2" />
                    <span>Diversification géographique</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <CheckCircle className="w-4 h-4 text-emerald-500 mr-2" />
                    <span>Gestion professionnelle</span>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Risque</span>
                    <span className="text-sm font-medium text-yellow-600">Modéré</span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-sm text-gray-500">Durée recommandée</span>
                    <span className="text-sm font-medium">8-12 ans</span>
                  </div>
                </div>
              </div>
            </div>

            {/* GFI */}
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
              <div className="relative h-64">
                <img
                  src="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=400&q=90"
                  alt="GFI - Fonds de Placement"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    GFI
                  </span>
                </div>
                <div className="absolute bottom-4 right-4">
                  <div className="stats-card">
                    <div className="text-blue-600 text-2xl font-bold">5.5%</div>
                    <div className="stats-label">Rendement</div>
                  </div>
                </div>
              </div>
              <div className="p-8">
                <h4 className="text-2xl font-bold text-gray-900 mb-4">
                  GFI - Fonds de Placement
                </h4>
                <p className="text-gray-600 mb-6">
                  Portefeuille diversifié d'actifs financiers gérés par des experts avec une performance optimisée.
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center text-sm">
                    <CheckCircle className="w-4 h-4 text-blue-500 mr-2" />
                    <span>Gestion active professionnelle</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <CheckCircle className="w-4 h-4 text-blue-500 mr-2" />
                    <span>Diversification internationale</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <CheckCircle className="w-4 h-4 text-blue-500 mr-2" />
                    <span>Liquidité quotidienne</span>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Risque</span>
                    <span className="text-sm font-medium text-orange-600">Élevé</span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-sm text-gray-500">Durée recommandée</span>
                    <span className="text-sm font-medium">5-10 ans</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Sukuk */}
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
              <div className="relative h-64">
                <img
                  src="https://images.unsplash.com/photo-1570129477492-45c003edd2be?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=400&q=90"
                  alt="Sukuk - Finance Islamique"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-amber-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    Sukuk
                  </span>
                </div>
                <div className="absolute bottom-4 right-4">
                  <div className="stats-card">
                    <div className="text-amber-600 text-2xl font-bold">7.0%</div>
                    <div className="stats-label">Rendement</div>
                  </div>
                </div>
              </div>
              <div className="p-8">
                <h4 className="text-2xl font-bold text-gray-900 mb-4">
                  Sukuk - Finance Islamique
                </h4>
                <p className="text-gray-600 mb-6">
                  Investissement conforme à la Sharia, basé sur des actifs tangibles et des principes éthiques stricts.
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center text-sm">
                    <CheckCircle className="w-4 h-4 text-amber-500 mr-2" />
                    <span>Conforme à la Sharia</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <CheckCircle className="w-4 h-4 text-amber-500 mr-2" />
                    <span>Actifs tangibles uniquement</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <CheckCircle className="w-4 h-4 text-amber-500 mr-2" />
                    <span>Rendements prévisibles</span>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Risque</span>
                    <span className="text-sm font-medium text-green-600">Faible</span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-sm text-gray-500">Durée recommandée</span>
                    <span className="text-sm font-medium">3-7 ans</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section avantages */}
          <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-3xl p-12 mb-16">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                Pourquoi Choisir Nos Produits ?
              </h3>
              <p className="text-lg text-gray-600">
                Des avantages uniques pour maximiser votre potentiel d'investissement
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-teal-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Sécurisé</h4>
                <p className="text-gray-600 text-sm">
                  Tous nos produits sont régulés et supervisés par les autorités financières compétentes.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-blue-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Performance</h4>
                <p className="text-gray-600 text-sm">
                  Des rendements attractifs et réguliers adaptés à votre profil de risque.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-amber-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Éthique</h4>
                <p className="text-gray-600 text-sm">
                  Investissements conformes aux principes éthiques et responsables.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-emerald-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Accompagnement</h4>
                <p className="text-gray-600 text-sm">
                  Support personnalisé par nos experts financiers tout au long de votre parcours.
                </p>
              </div>
            </div>
          </div>

          {/* Tableau comparatif détaillé */}
          <div className="bg-white rounded-3xl shadow-xl p-8 mb-16">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                Comparaison Détaillée des Produits
              </h3>
              <p className="text-lg text-gray-600">
                Analyse comparative pour vous aider à faire le meilleur choix
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">Critères</th>
                    <th className="text-center py-4 px-6 font-semibold text-emerald-600">SCPI</th>
                    <th className="text-center py-4 px-6 font-semibold text-blue-600">GFI</th>
                    <th className="text-center py-4 px-6 font-semibold text-amber-600">Sukuk</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 font-medium">Rendement annuel</td>
                    <td className="text-center py-4 px-6 text-emerald-600 font-bold">4.5%</td>
                    <td className="text-center py-4 px-6 text-blue-600 font-bold">5.5%</td>
                    <td className="text-center py-4 px-6 text-amber-600 font-bold">7.0%</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 font-medium">Niveau de risque</td>
                    <td className="text-center py-4 px-6">
                      <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm">Modéré</span>
                    </td>
                    <td className="text-center py-4 px-6">
                      <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm">Élevé</span>
                    </td>
                    <td className="text-center py-4 px-6">
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">Faible</span>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 font-medium">Investissement minimum</td>
                    <td className="text-center py-4 px-6">1 000 €</td>
                    <td className="text-center py-4 px-6">5 000 €</td>
                    <td className="text-center py-4 px-6">2 500 €</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 font-medium">Liquidité</td>
                    <td className="text-center py-4 px-6">Trimestrielle</td>
                    <td className="text-center py-4 px-6">Quotidienne</td>
                    <td className="text-center py-4 px-6">À l'échéance</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 font-medium">Frais de gestion</td>
                    <td className="text-center py-4 px-6">8-12%</td>
                    <td className="text-center py-4 px-6">1.5-2%</td>
                    <td className="text-center py-4 px-6">0.5-1%</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-6 font-medium">Durée recommandée</td>
                    <td className="text-center py-4 px-6">8-12 ans</td>
                    <td className="text-center py-4 px-6">5-10 ans</td>
                    <td className="text-center py-4 px-6">3-7 ans</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Section performances historiques */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-12 mb-16">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                Performances Historiques
              </h3>
              <p className="text-lg text-gray-600">
                Analyse des rendements sur les 5 dernières années
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* SCPI Performance */}
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="w-4 h-4 bg-emerald-500 rounded-full mr-3"></div>
                  <h4 className="text-xl font-bold text-gray-900">SCPI</h4>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">2023</span>
                    <span className="font-semibold text-emerald-600">4.8%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">2022</span>
                    <span className="font-semibold text-emerald-600">4.3%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">2021</span>
                    <span className="font-semibold text-emerald-600">4.7%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">2020</span>
                    <span className="font-semibold text-emerald-600">4.1%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">2019</span>
                    <span className="font-semibold text-emerald-600">4.6%</span>
                  </div>
                  <div className="border-t pt-4 mt-4">
                    <div className="flex justify-between font-bold">
                      <span>Moyenne 5 ans</span>
                      <span className="text-emerald-600">4.5%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* GFI Performance */}
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="w-4 h-4 bg-blue-500 rounded-full mr-3"></div>
                  <h4 className="text-xl font-bold text-gray-900">GFI</h4>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">2023</span>
                    <span className="font-semibold text-blue-600">6.2%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">2022</span>
                    <span className="font-semibold text-blue-600">4.1%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">2021</span>
                    <span className="font-semibold text-blue-600">7.8%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">2020</span>
                    <span className="font-semibold text-blue-600">3.9%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">2019</span>
                    <span className="font-semibold text-blue-600">5.7%</span>
                  </div>
                  <div className="border-t pt-4 mt-4">
                    <div className="flex justify-between font-bold">
                      <span>Moyenne 5 ans</span>
                      <span className="text-blue-600">5.5%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sukuk Performance */}
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="w-4 h-4 bg-amber-500 rounded-full mr-3"></div>
                  <h4 className="text-xl font-bold text-gray-900">Sukuk</h4>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">2023</span>
                    <span className="font-semibold text-amber-600">7.1%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">2022</span>
                    <span className="font-semibold text-amber-600">6.8%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">2021</span>
                    <span className="font-semibold text-amber-600">7.3%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">2020</span>
                    <span className="font-semibold text-amber-600">6.9%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">2019</span>
                    <span className="font-semibold text-amber-600">7.2%</span>
                  </div>
                  <div className="border-t pt-4 mt-4">
                    <div className="flex justify-between font-bold">
                      <span>Moyenne 5 ans</span>
                      <span className="text-amber-600">7.0%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section stratégies d'allocation */}
          <div className="bg-white rounded-3xl shadow-xl p-12 mb-16">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                Stratégies d'Allocation Recommandées
              </h3>
              <p className="text-lg text-gray-600">
                Répartition optimale selon votre profil d'investisseur
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Profil Conservateur */}
              <div className="border-2 border-green-200 rounded-2xl p-8 hover:border-green-300 transition-colors">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-8 h-8 text-green-600" />
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900">Conservateur</h4>
                  <p className="text-gray-600 mt-2">Préservation du capital</p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Sukuk</span>
                    <span className="font-bold text-amber-600">60%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-amber-500 h-2 rounded-full" style={{width: '60%'}}></div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">SCPI</span>
                    <span className="font-bold text-emerald-600">35%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-emerald-500 h-2 rounded-full" style={{width: '35%'}}></div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">GFI</span>
                    <span className="font-bold text-blue-600">5%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-500 h-2 rounded-full" style={{width: '5%'}}></div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Rendement attendu</span>
                    <span className="font-bold text-green-600">5.8%</span>
                  </div>
                  <div className="flex justify-between mt-2">
                    <span className="text-gray-600">Risque</span>
                    <span className="text-green-600">Faible</span>
                  </div>
                </div>
              </div>

              {/* Profil Modéré */}
              <div className="border-2 border-blue-200 rounded-2xl p-8 hover:border-blue-300 transition-colors">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Target className="w-8 h-8 text-blue-600" />
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900">Modéré</h4>
                  <p className="text-gray-600 mt-2">Équilibre risque/rendement</p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">SCPI</span>
                    <span className="font-bold text-emerald-600">45%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-emerald-500 h-2 rounded-full" style={{width: '45%'}}></div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Sukuk</span>
                    <span className="font-bold text-amber-600">35%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-amber-500 h-2 rounded-full" style={{width: '35%'}}></div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">GFI</span>
                    <span className="font-bold text-blue-600">20%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-500 h-2 rounded-full" style={{width: '20%'}}></div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Rendement attendu</span>
                    <span className="font-bold text-blue-600">6.2%</span>
                  </div>
                  <div className="flex justify-between mt-2">
                    <span className="text-gray-600">Risque</span>
                    <span className="text-blue-600">Modéré</span>
                  </div>
                </div>
              </div>

              {/* Profil Dynamique */}
              <div className="border-2 border-orange-200 rounded-2xl p-8 hover:border-orange-300 transition-colors">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Zap className="w-8 h-8 text-orange-600" />
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900">Dynamique</h4>
                  <p className="text-gray-600 mt-2">Croissance du capital</p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">GFI</span>
                    <span className="font-bold text-blue-600">50%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-500 h-2 rounded-full" style={{width: '50%'}}></div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">SCPI</span>
                    <span className="font-bold text-emerald-600">30%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-emerald-500 h-2 rounded-full" style={{width: '30%'}}></div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Sukuk</span>
                    <span className="font-bold text-amber-600">20%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-amber-500 h-2 rounded-full" style={{width: '20%'}}></div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Rendement attendu</span>
                    <span className="font-bold text-orange-600">6.8%</span>
                  </div>
                  <div className="flex justify-between mt-2">
                    <span className="text-gray-600">Risque</span>
                    <span className="text-orange-600">Élevé</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="bg-gray-50 rounded-3xl p-12 mb-16">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                Questions Fréquemment Posées
              </h3>
              <p className="text-lg text-gray-600">
                Tout ce que vous devez savoir sur nos produits d'investissement
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-4">
              {[
                {
                  question: "Quel est le montant minimum pour commencer à investir ?",
                  answer: "Le montant minimum varie selon le produit : 1 000€ pour les SCPI, 5 000€ pour les GFI, et 2 500€ pour les Sukuk. Nous recommandons cependant de diversifier avec un capital d'au moins 10 000€."
                },
                {
                  question: "Comment puis-je récupérer mon investissement ?",
                  answer: "La liquidité dépend du produit choisi. Les GFI offrent une liquidité quotidienne, les SCPI permettent des rachats trimestriels, et les Sukuk arrivent à échéance selon leur durée prédéfinie."
                },
                {
                  question: "Y a-t-il des frais cachés ?",
                  answer: "Non, tous nos frais sont transparents et détaillés dans les documents contractuels. Ils incluent les frais de gestion, d'entrée et de sortie, clairement indiqués pour chaque produit."
                },
                {
                  question: "Quelle est la fiscalité applicable ?",
                  answer: "La fiscalité varie selon le produit et votre situation. Les SCPI bénéficient du régime des revenus fonciers, les GFI de la flat tax ou du barème progressif, et les Sukuk suivent la fiscalité des obligations."
                },
                {
                  question: "Comment SOeasi sélectionne-t-elle ses produits ?",
                  answer: "Nous appliquons un processus de due diligence rigoureux, analysant la performance historique, la qualité de gestion, la conformité réglementaire et l'alignement avec nos principes éthiques."
                }
              ].map((faq, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    className="w-full text-left p-6 flex justify-between items-center hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-semibold text-gray-900">{faq.question}</span>
                    {expandedFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </button>
                  {expandedFaq === index && (
                    <div className="px-6 pb-6">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Section témoignages */}
          <div className="bg-gradient-to-br from-teal-600 to-blue-700 rounded-3xl p-12 mb-16 text-white">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold mb-4">
                Ce Que Disent Nos Clients
              </h3>
              <p className="text-xl text-teal-100">
                L'expérience de nos investisseurs avec SOeasi
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                <div className="flex items-center mb-6">
                  <img
                    src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&h=100&q=80"
                    alt="Client"
                    className="w-12 h-12 rounded-full mr-4"
                  />
                  <div>
                    <h4 className="font-semibold">Pierre Martin</h4>
                    <p className="text-teal-200 text-sm">Chef d'entreprise</p>
                  </div>
                </div>
                <p className="text-teal-100 leading-relaxed">
                  "Grâce à SOeasi, j'ai pu diversifier mes investissements tout en respectant mes valeurs.
                  Le rendement de 6.5% sur mon portefeuille mixte dépasse mes attentes."
                </p>
                <div className="flex mt-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                  ))}
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                <div className="flex items-center mb-6">
                  <img
                    src="https://images.unsplash.com/photo-1494790108755-2616b2e2f0f5?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&h=100&q=80"
                    alt="Client"
                    className="w-12 h-12 rounded-full mr-4"
                  />
                  <div>
                    <h4 className="font-semibold">Sarah Dubois</h4>
                    <p className="text-teal-200 text-sm">Médecin</p>
                  </div>
                </div>
                <p className="text-teal-100 leading-relaxed">
                  "L'approche éthique de SOeasi correspond parfaitement à mes convictions.
                  Les Sukuk m'offrent une tranquillité d'esprit avec un rendement stable de 7%."
                </p>
                <div className="flex mt-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                  ))}
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                <div className="flex items-center mb-6">
                  <img
                    src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&h=100&q=80"
                    alt="Client"
                    className="w-12 h-12 rounded-full mr-4"
                  />
                  <div>
                    <h4 className="font-semibold">Marc Lefebvre</h4>
                    <p className="text-teal-200 text-sm">Ingénieur</p>
                  </div>
                </div>
                <p className="text-teal-100 leading-relaxed">
                  "Portfolio équilibré SCPI/GFI depuis 3 ans. Performance constante et équipe
                  toujours disponible pour mes questions. Je recommande vivement SOeasi."
                </p>
                <div className="flex mt-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <div className="bg-white rounded-2xl shadow-lg p-12 max-w-4xl mx-auto">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Prêt à Commencer Votre Analyse ?
              </h3>
              <p className="text-lg text-gray-600 mb-8">
                Découvrez quel produit correspond le mieux à votre profil et à vos objectifs financiers grâce à notre simulateur intelligent.
              </p>

              <button
                onClick={onStartSimulator}
                className="inline-flex items-center space-x-3 bg-gradient-to-r from-teal-600 to-blue-600 text-white px-10 py-4 rounded-xl font-bold text-lg hover:from-teal-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <TrendingUp className="w-6 h-6" />
                <span>Commencer mon analyse</span>
                <ArrowRight className="w-5 h-5" />
              </button>

              <p className="text-sm text-gray-500 mt-4">
                ✓ Gratuit et sans engagement • ✓ Résultats en 5 minutes
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProduitsExplication;